// Phase 5 T23 — full ROI calculator panel controller (replaces the
// Phase 1 stub that only ate submit clicks to keep the disabled
// button from posting to the page URL).
//
// Behaviors:
//   * On industry change, prefill avg_deal_size with the category
//     midpoint and switch the time-horizon radio to the category's
//     default UNLESS the user has already touched those fields
//     (markDealSizeEdited / markHorizonEdited set a `dataset.userEdited`
//     flag so prefill never overwrites a user's input).
//   * On submit, POST the form via fetch with Accept text/vnd.turbo-stream.html;
//     server returns a Turbo Stream that swaps #roi-output. We render it
//     via Turbo.renderStreamMessage so the rest of the form stays mounted
//     (preserves focus + scroll position).
//
// [v1.1 FIX #13] Turbo MUST be imported explicitly — same lesson as T17.
import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = [
    "industryDropdown", "horizonRadios", "totalCostInput", "dealSizeInput",
    "closeRateInput", "submit", "spinner", "errors", "horizonNote",
    // LRPO-5471 — line-item inputs + the "Calculated from line items" note, so
    // the Total field's read-only state can track line items live before Calculate.
    // (round 3) totalLockIcon = the lock glyph overlaid on the field's trailing
    // edge, toggled with the read-only state as the clear visual "locked" cue.
    "itemizedInput", "totalCalculatedNote", "totalLockIcon"
  ]
  static values = {
    benchmarks:       { type: Object, default: {} },
    calculateUrl:     String,
    eventExhibitorId: Number
  }

  connect() {
    // LRPO-5269 — remember the template's default (network) error copy so the
    // generic failure path can restore it after showing a field-specific
    // validation message (see _showErrors). Captured as text (never innerHTML)
    // so the alert is only ever written via textContent.
    this._defaultErrorText = this.hasErrorsTarget ? this.errorsTarget.textContent.trim() : ""

    if (this.hasDealSizeInputTarget && !this.dealSizeInputTarget.value) {
      this.prefillBenchmark()
    }

    // LRPO-5462 — reflect the initial category's horizon-scaling state even
    // when the deal-size prefill above is skipped (persisted record).
    this._syncHorizonNote()

    // LRPO-5471 (round 4) — the value the Total field reverts to when the LAST
    // line item is cleared (see syncTotalReadonly). Seeded from the first paint:
    // if the server rendered the field read-only it did so because the record
    // already has line items, which means the value showing is a COMPUTED sum,
    // NOT something the user typed — so there is no manual total to go back to
    // and the field must end up EMPTY. Only an editable field's initial value is
    // a genuine manual total worth preserving.
    this._manualTotal =
      this.hasTotalCostInputTarget && !this.totalCostInputTarget.readOnly
        ? this.totalCostInputTarget.value
        : ""
  }

  prefillBenchmark() {
    // LRPO-5462 — keep the "time horizon doesn't scale results" note honest as
    // the user switches industries before Calculate.
    this._syncHorizonNote()

    const cat   = this.industryDropdownTarget.value
    const bench = this.benchmarksValue[cat]
    if (!bench) return

    if (bench.suggest && !this.dealSizeInputTarget.dataset.userEdited) {
      if (bench.midpoint != null) {
        this.dealSizeInputTarget.value = bench.midpoint
      }
      if (bench.range) {
        this.dealSizeInputTarget.placeholder =
          `Suggested: $${bench.range.low}–$${bench.range.high}`
      }
    }

    if (bench.default_horizon && !this.element.dataset.horizonUserSet) {
      const radio = this.horizonRadiosTargets.find(
        (r) => Number(r.value) === bench.default_horizon
      )
      if (radio) radio.checked = true
    }
  }

  // LRPO-5462 — show the clarification note only for categories with no
  // reorder-multiplier table (the same suggest:false categories the server's
  // RoiBenchmarksService#benchmark_scales_with_horizon? predicate flags). The
  // benchmarks-value payload carries `multipliers` per category, so we derive
  // "does it scale?" from the data — never a hardcoded list. No-ops safely when
  // the note target isn't present (e.g. the standalone controller unit tests).
  _syncHorizonNote() {
    if (!this.hasHorizonNoteTarget) return

    const cat    = this.hasIndustryDropdownTarget ? this.industryDropdownTarget.value : null
    const bench  = cat ? this.benchmarksValue[cat] : null
    const scales = !!(bench && bench.multipliers && Object.keys(bench.multipliers).length > 0)

    this.horizonNoteTarget.classList.toggle("d-none", scales)
  }

  // LRPO-5471 — keep the Total field's CALCULATED / read-only state honest as the
  // user types line items, without waiting for a Calculate round-trip. "Present"
  // mirrors the server predicate (ExhibitorEventCost#itemized_present?): any line
  // item with a positive numeric value. readonly (never disabled) so the field
  // still submits; the model recomputes the total from the line items on save.
  // Guarded so it no-ops on renders/tests without the totalCost/note targets.
  syncTotalReadonly() {
    if (!this.hasTotalCostInputTarget) return

    const present = this._itemizedPresent()

    // LRPO-5471 (round 4) — the field's CURRENT read-only flag is the state
    // BEFORE this sync, which is what lets us detect the two edge transitions.
    const wasLocked = this.totalCostInputTarget.readOnly

    // Entering the locked state: stash the manual total the user had typed so the
    // line-item sum that is about to overwrite it isn't a destructive edit.
    if (present && !wasLocked) {
      this._manualTotal = this.totalCostInputTarget.value
    }

    this.totalCostInputTarget.readOnly = present

    // LRPO-5471 (round 3) — mirror the server's locked-state affordance live: the
    // muted fill on the field, the trailing lock glyph, and the emphasized note
    // all toggle in lock-step with readOnly so there's an unmistakable cue the
    // field is calculated (not just silently non-editable).
    this.totalCostInputTarget.classList.toggle("roi-total-cost--locked", present)
    if (this.hasTotalLockIconTarget) {
      this.totalLockIconTarget.classList.toggle("d-none", !present)
    }
    if (this.hasTotalCalculatedNoteTarget) {
      this.totalCalculatedNoteTarget.classList.toggle("d-none", !present)
    }

    // LRPO-5471 (round 3) — reflect the running line-item sum in the Total field
    // immediately, instead of leaving a stale value until the Calculate
    // round-trip. Assigning `.value` does not fire an `input` event, so this
    // won't re-trigger telemetry.
    //
    // LRPO-5471 (round 4 — QA return) — round 3 had NO else branch here, so when
    // the last line item was deleted the field simply kept whatever sum it was
    // last given: it unlocked while still "prefilled with the last line item
    // value instead of being cleared" (Elmira). The line items are a TRANSIENT
    // override of the Total, so releasing that override must put the field back
    // the way it was before they took it over — the user's stashed manual total,
    // or empty when there never was one (the reported case, and every persisted
    // itemized record, whose displayed total is a computed sum).
    if (present) {
      this.totalCostInputTarget.value = this._itemizedSum()
    } else if (wasLocked) {
      this.totalCostInputTarget.value = this._manualTotal ?? ""
    }
  }

  // LRPO-5471 — "are line items in play?", mirroring the server predicate
  // ExhibitorEventCost#itemized_present? (`any? { present? && positive? }`): any
  // line item with a positive value. Single definition so the live lock state and
  // the post-save stash invalidation below can never drift apart from each other —
  // or from the model that decides whether to override total_cost.
  _itemizedPresent() {
    return this.hasItemizedInputTarget &&
      this.itemizedInputTargets.some((el) => Number(el.value) > 0)
  }

  // LRPO-5471 (round 3) — sum the line items, formatted to match the server's
  // roi_number_field_value helper: whole numbers render without a trailing ".0",
  // fractional values keep up to two decimals (float noise rounded away).
  _itemizedSum() {
    const sum = this.itemizedInputTargets.reduce(
      (acc, el) => acc + (Number(el.value) || 0),
      0
    )
    return parseFloat(sum.toFixed(2)).toString()
  }

  markDealSizeEdited() {
    this.dealSizeInputTarget.dataset.userEdited = "1"
  }

  markHorizonEdited() {
    this.element.dataset.horizonUserSet = "1"
  }

  async calculate(event) {
    event.preventDefault()

    this.submitTarget.disabled = true
    this.spinnerTarget.classList.remove("d-none")
    this._hideErrors()

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: {
          "Accept":       "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrf
        },
        body: new FormData(this.element)
      })

      // LRPO-5269 / LRPO-5267 — a validation failure (e.g. a category_notes /
      // notes paste past the TEXT column's 65,535-byte ceiling) comes back as
      // 422 with a JSON { errors: [...] } body, NOT a turbo-stream. The old code
      // fell straight through to Turbo.renderStreamMessage, which silently
      // no-op'd on JSON — so the user saw nothing (the reported bug). Surface the
      // server's messages in the error alert instead.
      if (response.status === 422) {
        this._showErrors(await this._parseErrors(response))
        return
      }

      if (!response.ok) {
        this._showErrors()
        return
      }

      Turbo.renderStreamMessage(await response.text())

      // LRPO-5471 (round 5 — review return) — a successful Calculate WITH line items
      // makes the model fold their sum into total_cost (calculate_total_if_itemized),
      // so the record's total IS that sum now and the pre-line-item manual figure the
      // user typed no longer exists anywhere. Retire the stash: the Calculate response
      // only turbo_stream.updates #roi-total-cost-field, which sits INSIDE this
      // controller's <form> element, so the controller never reconnects and connect()'s
      // seeding never re-runs — the stash would otherwise outlive the save that
      // superseded it and get written back into the field when the last line item is
      // deleted (and re-saved by the next Calculate, computing ROI from a spend figure
      // the user had already replaced). It also made the app self-inconsistent: after
      // an F5 the same delete cleared correctly, because the server-locked field seeds
      // an empty stash.
      //
      // Derived from the line-item inputs (which live outside the swapped node, and are
      // the same inputs we just posted) rather than by re-reading the freshly swapped
      // total field — Turbo renders the stream asynchronously, so that node may still
      // be the OLD one at this point.
      //
      // Scoped to "line items were actually in play" as DEFENCE IN DEPTH, not because
      // an unconditional blank would misbehave today: a no-line-items Calculate comes
      // back with the field still EDITABLE holding the persisted total, so the next
      // takeover re-reads that value off the live field anyway (see the "captures the
      // manual total at the takeover" spec) and the restore survives either way. The
      // guard is what keeps that true if the response ever stops re-rendering the total
      // field — without it the restore would then blank a manual total the user never
      // replaced, which IS this ticket's original "edit silently discarded" bug.
      if (this._itemizedPresent()) this._manualTotal = ""
    } catch (e) {
      this._showErrors()
      console.error("reports-roi-panel: calculate failed", e)
    } finally {
      this.submitTarget.disabled = false
      this.spinnerTarget.classList.add("d-none")
    }
  }

  // LRPO-5269 — pull the { errors: [...] } list out of a 422 JSON body,
  // tolerating a non-JSON body (returns [] so the caller shows the generic
  // network fallback rather than throwing).
  async _parseErrors(response) {
    try {
      const payload = await response.json()
      return Array.isArray(payload?.errors) ? payload.errors.filter(Boolean) : []
    } catch (_) {
      return []
    }
  }

  // LRPO-5269 — reveal the error alert. With messages, render the server's
  // validation errors (one line, or a bullet list for several); without, restore
  // the template's default network-error copy. Only ever writes via textContent
  // / created elements, never innerHTML, so server-supplied strings can't inject
  // markup.
  _showErrors(messages = []) {
    if (!this.hasErrorsTarget) return

    if (messages.length === 1) {
      this.errorsTarget.textContent = messages[0]
    } else if (messages.length > 1) {
      this.errorsTarget.textContent = ""
      const ul = document.createElement("ul")
      ul.className = "mb-0 ps-3"
      messages.forEach((msg) => {
        const li = document.createElement("li")
        li.textContent = msg
        ul.appendChild(li)
      })
      this.errorsTarget.appendChild(ul)
    } else {
      this.errorsTarget.textContent = this._defaultErrorText
    }

    this.errorsTarget.classList.remove("d-none")
  }

  _hideErrors() {
    if (this.hasErrorsTarget) this.errorsTarget.classList.add("d-none")
  }
}
